import { Component, Input, OnChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ParticipantMarketType, ParticipantOption, ParticipantPriceType } from '@cds/betting-offer';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { FixtureParticipantPriceMap, HorseRaceSortingDirection, WinGameFixture, WinGameParticipant } from '@frontend/sports/racing/feature/models';

import { CrmPromotionProviderService, PromotionMap } from '../crm-promotion/crm-promotion-provider.service';
import { HorsesFixtureParticipantHelper } from '../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../horse-racing-shared/services/horses-fixture-sorting-helper.service';

export interface LinkedOptionGroupViewModel {
    fixture?: WinGameFixture;
    participants: WinGameParticipant[];
    options: FixtureParticipantPriceMap;
    parentLinkedEventId?: string;
}

/**
 * Displaying linked TV2 Golf EW market mapped as TV1 Golf Option Group, allowing Golf EW bets on bet-station
 * Configurable dynacon (BettingOffer.GolfSiblingMapping) - mapping between TV1 templateId and TV2 MarketType. Empty as switch off.
 * Reusing horse racing logic for handling the @fixture
 */
@Component({
    template: '',
    standalone: false,
})
export class BaseLinkedGroupComponent implements OnChanges {
    private static readonly emptyViewModel: LinkedOptionGroupViewModel = {
        fixture: undefined,
        participants: [],
        options: {},
    };

    @Input() fixture: WinGameFixture;
    @Input() groupName: string;
    @Input() crmPromotedOptionIdsMap?: { [optionId: number]: number } = {};
    @Input() parentLinkedEventId?: string;

    vm: LinkedOptionGroupViewModel = BaseLinkedGroupComponent.emptyViewModel;

    constructor(
        public sitecore: Sitecore,
        protected horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        protected crmPromotionProvider: CrmPromotionProviderService,
    ) {
        this.crmPromotionProvider.promotionsMap$.pipe(takeUntilDestroyed()).subscribe((ids) => this.promotionMapChanged(ids));
    }

    ngOnChanges(changes: ISimpleChanges<BaseLinkedGroupComponent>): void {
        if (changes.fixture?.currentValue) {
            this.updateViewModel(changes.fixture.currentValue);
        } else {
            this.vm = BaseLinkedGroupComponent.emptyViewModel;
        }
    }

    isLocked(price: ParticipantOption | undefined, participant: WinGameParticipant): boolean {
        return !this.vm.fixture || this.horsesFixturePricingHelper.isPriceLocked(this.vm.fixture, participant, price);
    }

    private updateViewModel(fixture: WinGameFixture): void {
        const options = this.horsesFixturePricingHelper.getPrices(fixture, ParticipantMarketType.Win, ParticipantPriceType.Fixed);
        let participants = this.horsesFixtureParticipantHelper.getParticipants(fixture.participants);
        participants = this.horsesFixtureSortingHelper.sortParticipantsBy(participants, options, HorseRaceSortingDirection.Odds);

        this.vm = {
            fixture,
            participants,
            options,
        };
    }

    private promotionMapChanged(promoMap: PromotionMap): void {
        this.crmPromotedOptionIdsMap = promoMap.options;
    }
}
